import { motion, type Variants } from "framer-motion";
import type { ReactNode } from "react";

/**
 * Motion for the whole panel.
 *
 * Three rules, from the design system already in index.css and from the
 * ui-ux-pro-max animation guidance:
 *
 *   1. **150–300ms for micro-interactions.** Longer reads as lag; shorter reads
 *      as a glitch. `EASE` is an expo-out that decelerates into place rather
 *      than stopping dead, which is what makes a transition feel responsive
 *      rather than animated.
 *   2. **transform and opacity only.** Animating width, height or top/left
 *      triggers layout on every frame and drops the panel to a slide show on
 *      the phones it is mostly read on. translateY + opacity composites.
 *   3. **Nothing animates without being asked, and everything stops under
 *      prefers-reduced-motion.** The `Reveal` component wraps framer-motion's
 *      `useReducedMotion`, so a user who has asked their OS to minimise motion
 *      gets static content with the spacing already in place.
 */

/** expo-out: fast to leave, gentle to arrive. */
export const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** Rise 16px into place. The single entrance used across the panel. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
};

/** Parent variant that staggers its children by `staggerChildren`. */
export const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

/**
 * One entrance animation for a block of content.
 *
 * `as` is not used — a `motion.div` is always a `div`, so the element type
 * never lies about what it renders.
 */
export function Reveal({
  children,
  delay = 0,
  y = 16,
  className,
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/**
 * A page-level entrance. The dashboard and every other screen are wrapped in
 * this so the first paint after a navigation is a settle, not a flash.
 */
export function PageEnter({ children }: { children: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}
